import {
  CASE_NOTE_MAX,
  type CaseDetail,
  type CaseItemView,
  DETECTED_KIND_LABELS,
  PIPELINE_STATUS_LABELS,
  type SchemaSnapshot,
} from '@hoopoe/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, CalendarRange, FileText, FolderKanban, Lock, MessagesSquare, SearchX, StickyNote, Users } from 'lucide-react';
import { type FormEvent, type ReactNode, useId, useState } from 'react';
import { DataBlock, DataNumber, DataText } from '../components/data/data-text';
import { EntityLink } from '../components/entities/entity-link';
import { Combobox } from '../components/guidance/combobox';
import { EmptyState } from '../components/guidance/empty-state';
import { EntityAutocomplete } from '../components/guidance/entity-autocomplete';
import { ErrorState } from '../components/guidance/error-state';
import { FieldHelp } from '../components/guidance/field-help';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { Button, buttonVariants } from '../components/ui/button';
import { useConfirm } from '../components/ui/confirm';
import { ApiError, api } from '../lib/api';
import { caseActorIds, useCase, useCaseWrite } from '../lib/cases';
import { cn } from '../lib/cn';
import { useEntitySearch } from '../lib/entities';
import { formatDate, type LibraryPage } from '../lib/library';
import { useSchema } from '../lib/schema';
import { useCan } from '../lib/session';

// Case workspace (PLAN §2 `case`, §4 `case_items`, §8; task 33): one investigation's entities, Library documents
// (sources) and notes, each with a note on why it is there, and the Case's own chat. Editors file and remove items
// and write notes; everyone reads. Removing an item takes it out of the Case only.

export const Route = createFileRoute('/cases/$id')({ component: CaseWorkspace });

const TOUR = {
  id: 'case-workspace',
  steps: [
    {
      target: '[data-tour="case-entities"]',
      title: 'Entities',
      body: 'Actors, events, places and assets that matter to this investigation. Add a note to say why each one is here.',
      learnMore: 'cases',
    },
    {
      target: '[data-tour="case-sources"]',
      title: 'Sources',
      body: 'Library documents filed in the Case. The chat of the Case reads them, and the documents that mention its entities.',
    },
    {
      target: '[data-tour="case-chat"]',
      title: 'Ask the chat about this Case',
      body: 'Answers come only from what the Case holds and links to, each with its sources.',
    },
  ],
};

const textarea =
  'min-h-20 w-full rounded-md border border-input bg-surface px-3 py-2 text-sm placeholder:text-muted-foreground aria-[invalid=true]:border-danger';

function CaseWorkspace() {
  useScreenTour(TOUR);
  const { id } = Route.useParams();
  const { data: snap } = useSchema();
  const q = useCase(id);
  if (q.isError)
    return q.error instanceof ApiError && q.error.status === 404 ? (
      <EmptyState icon={SearchX} title="This Case doesn’t exist" learnMore="cases">
        <Link to="/cases" className="underline">
          Back to Cases
        </Link>
      </EmptyState>
    ) : (
      <ErrorState error={q.error} title="The Case couldn’t be loaded" onRetry={() => q.refetch()} />
    );
  if (!q.data || !snap)
    return (
      <div className="space-y-3" aria-busy="true">
        <span className="sr-only">Loading…</span>
        <div className="h-24 animate-pulse rounded-lg bg-muted" />
        <div className="h-64 animate-pulse rounded-lg bg-muted" />
      </div>
    );
  return <Workspace detail={q.data} snap={snap} />;
}

function Workspace({ detail, snap }: { detail: CaseDetail; snap: SchemaSnapshot }) {
  const canWrite = useCan('content.write');
  const kase = detail.case;
  const entities = detail.items.filter(i => i.kind === 'entity');
  const sources = detail.items.filter(i => i.kind === 'document');
  const notes = detail.items.filter(i => i.kind === 'note');
  const actors = caseActorIds(detail.items);
  return (
    <div className="space-y-4">
      <Link to="/cases" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" aria-hidden="true" /> Cases
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <FolderKanban className="size-6 shrink-0 text-muted-foreground" aria-hidden="true" />
            <DataText>{kase.displayName}</DataText>
            {kase.sensitivity === 'confidential' && <Lock className="size-4 text-muted-foreground" aria-label="Confidential" role="img" />}
          </h1>
          <p className="text-sm text-muted-foreground">
            <span className="font-mono">{kase.code}</span> · <DataNumber>{detail.counts.entities}</DataNumber>{' '}
            {detail.counts.entities === 1 ? 'entity' : 'entities'} · <DataNumber>{detail.counts.documents}</DataNumber>{' '}
            {detail.counts.documents === 1 ? 'source' : 'sources'} · <DataNumber>{detail.counts.notes}</DataNumber>{' '}
            {detail.counts.notes === 1 ? 'note' : 'notes'} ·{' '}
            <Link to="/entity/$id" params={{ id: kase.id }} className="underline underline-offset-2">
              Case details
            </Link>
          </p>
          {kase.summary && <DataBlock className="max-w-3xl text-sm">{kase.summary}</DataBlock>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/chat" search={{ case: kase.id }} className={buttonVariants({ size: 'md' })} data-tour="case-chat">
            <MessagesSquare aria-hidden="true" /> Ask the chat about this Case
          </Link>
          {actors.length > 0 && (
            <Link to="/timeline" search={{ ids: actors.join(',') }} className={buttonVariants({ variant: 'outline', size: 'md' })}>
              <CalendarRange aria-hidden="true" /> Open timeline
            </Link>
          )}
        </div>
      </div>
      <Tip id="case-workspace" learnMore="cases">
        Removing an item takes it out of this Case only; the entity or document stays in Hoopoe. Every change is recorded in the audit log.
      </Tip>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-4">
          <Section
            id="case-entities"
            icon={Users}
            title="Entities"
            count={entities.length}
            help="Actors, events, places, assets and other Cases that belong to this investigation."
          >
            {canWrite && <AddEntity caseId={kase.id} snap={snap} />}
            <Items caseId={kase.id} items={entities} snap={snap} empty="No entity in this Case yet." />
          </Section>
          <Section
            id="case-sources"
            icon={FileText}
            title="Sources"
            count={sources.length}
            help="Library documents filed in this Case: reports, transcripts, notes. They stay in their collections too."
          >
            {canWrite && <AddDocument caseId={kase.id} />}
            <Items caseId={kase.id} items={sources} snap={snap} empty="No document in this Case yet." />
          </Section>
          <Section
            id="case-notes"
            icon={StickyNote}
            title="Notes"
            count={notes.length}
            help="Your team’s working notes: leads, open questions, next steps."
          >
            {canWrite && <AddNote caseId={kase.id} />}
            <Items caseId={kase.id} items={notes} snap={snap} empty="No note yet." />
          </Section>
        </div>
        <aside className="space-y-4">
          <section aria-labelledby="case-chats" className="space-y-3 rounded-lg border bg-surface p-4">
            <h2 id="case-chats" className="font-semibold">
              Your conversations about this Case
            </h2>
            {detail.chats.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                None yet. The chat of a Case answers only from its entities, their documents and the sources filed here.
              </p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {detail.chats.map(c => (
                  <li key={c.id}>
                    <Link to="/chat" search={{ session: c.id }} className="font-medium hover:underline">
                      <DataText>{c.title}</DataText>
                    </Link>
                    <span className="text-xs text-muted-foreground"> · {formatDate(c.updatedAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

function Section({
  id,
  icon: Icon,
  title,
  count,
  help,
  children,
}: {
  id: string;
  icon: typeof Users;
  title: string;
  count: number;
  help: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={`${id}-heading`} data-tour={id} className="space-y-3 rounded-lg border bg-surface p-4">
      <h2 id={`${id}-heading`} className="flex items-center gap-1.5 font-semibold">
        <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
        {title}
        {count > 0 && <DataNumber className="rounded-full bg-muted px-1.5 text-xs font-normal">{count}</DataNumber>}
        <FieldHelp label={title} help={help} learnMore="cases" />
      </h2>
      {children}
    </section>
  );
}

function useAddFeedback() {
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const view = message && (
    <p role={message.ok ? 'status' : 'alert'} className={cn('text-xs', message.ok ? 'text-muted-foreground' : 'text-danger-foreground')}>
      <DataText>{message.text}</DataText>
    </p>
  );
  return { setMessage, view };
}

function AddEntity({ caseId, snap }: { caseId: string; snap: SchemaSnapshot }) {
  const { add } = useCaseWrite(caseId);
  const search = useEntitySearch(snap, caseId);
  const [round, setRound] = useState(0);
  const fb = useAddFeedback();
  const inputId = useId();
  return (
    <div className="space-y-1">
      <label htmlFor={inputId} className="text-xs font-medium">
        Add an entity
      </label>
      <EntityAutocomplete
        key={round}
        id={inputId}
        placeholder="Type a name, alias or ID code, in any script"
        search={text => search(null, text)}
        onSelect={async s => {
          fb.setMessage(null);
          try {
            await add.mutateAsync({ entityId: s.id });
            fb.setMessage({ ok: true, text: `Added ${s.name}.` });
            setRound(r => r + 1);
          } catch (e) {
            fb.setMessage({ ok: false, text: e instanceof ApiError ? e.message : 'It could not be added. Try again.' });
          }
        }}
      />
      {fb.view}
    </div>
  );
}

interface DocOption {
  id: string;
  label: string;
  detail: string;
}

function AddDocument({ caseId }: { caseId: string }) {
  const { add } = useCaseWrite(caseId);
  const [round, setRound] = useState(0);
  const fb = useAddFeedback();
  const inputId = useId();
  return (
    <div className="space-y-1">
      <label htmlFor={inputId} className="text-xs font-medium">
        Add a document from the Library
      </label>
      <Combobox<DocOption>
        key={round}
        id={inputId}
        placeholder="Type words from its name or text"
        minChars={2}
        emptyText="No document found"
        search={async text => {
          const res = await api<LibraryPage>(`/library/documents?${new URLSearchParams({ q: text, limit: '8' })}`).catch(() => null);
          return (res?.documents ?? []).map(d => ({
            id: d.id,
            label: d.title ?? d.filename,
            detail: `${DETECTED_KIND_LABELS[d.kind]} · ${formatDate(d.uploadedAt)}`,
          }));
        }}
        getKey={d => d.id}
        getLabel={d => d.label}
        renderOption={d => (
          <span className="min-w-0 truncate">
            <DataText className="font-medium">{d.label}</DataText>
            <span className="text-muted-foreground"> · {d.detail}</span>
          </span>
        )}
        onSelect={async d => {
          fb.setMessage(null);
          try {
            await add.mutateAsync({ documentId: d.id });
            fb.setMessage({ ok: true, text: `Added ${d.label}.` });
            setRound(r => r + 1);
          } catch (e) {
            fb.setMessage({ ok: false, text: e instanceof ApiError ? e.message : 'It could not be added. Try again.' });
          }
        }}
      />
      {fb.view}
    </div>
  );
}

function AddNote({ caseId }: { caseId: string }) {
  const { add } = useCaseWrite(caseId);
  const [text, setText] = useState('');
  const fb = useAddFeedback();
  const inputId = useId();
  const tooLong = text.length > CASE_NOTE_MAX;
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!text.trim() || tooLong) return;
    fb.setMessage(null);
    try {
      const sent = text;
      await add.mutateAsync({ note: sent });
      // Keep anything typed while the note was being saved.
      setText(t => (t === sent ? '' : t));
      fb.setMessage({ ok: true, text: 'Note added.' });
    } catch (err) {
      fb.setMessage({ ok: false, text: err instanceof ApiError ? err.message : 'The note could not be added. Try again.' });
    }
  }
  return (
    <form onSubmit={submit} className="space-y-2">
      <label htmlFor={inputId} className="text-xs font-medium">
        Write a note
      </label>
      <textarea
        id={inputId}
        value={text}
        onChange={e => setText(e.target.value)}
        dir="auto"
        className={textarea}
        aria-invalid={tooLong || undefined}
        placeholder="A lead, an open question, a next step…"
      />
      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={!text.trim() || tooLong || add.isPending}>
          Add note
        </Button>
        {tooLong && <span className="text-xs text-danger-foreground">Keep it under {CASE_NOTE_MAX.toLocaleString('en')} characters.</span>}
      </div>
      {fb.view}
    </form>
  );
}

function Items({ caseId, items, snap, empty }: { caseId: string; items: CaseItemView[]; snap: SchemaSnapshot; empty: string }) {
  if (!items.length) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return (
    <ul className="divide-y">
      {items.map(i => (
        <Item key={i.id} caseId={caseId} item={i} snap={snap} />
      ))}
    </ul>
  );
}

function Item({ caseId, item: i, snap }: { caseId: string; item: CaseItemView; snap: SchemaSnapshot }) {
  const canWrite = useCan('content.write');
  const confirm = useConfirm();
  const { edit, remove } = useCaseWrite(caseId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(i.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const noteId = useId();
  const name = i.entity?.displayName ?? i.document?.title ?? i.document?.filename ?? 'this note';
  const tooLong = draft.length > CASE_NOTE_MAX;

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await edit.mutateAsync({ itemId: i.id, note: draft });
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The note could not be saved. Try again.');
    }
  }
  async function drop() {
    const ok = await confirm({
      title: i.kind === 'note' ? 'Remove this note?' : `Remove ${name} from the Case?`,
      body:
        i.kind === 'note'
          ? 'The note is taken out of the Case. Its text stays in the Case’s activity log.'
          : 'It is taken out of this Case only; it stays in Hoopoe and in any other Case.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(i.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'It could not be removed. Try again.');
    }
  }
  const by = [i.createdBy ? `Added by ${i.createdBy}` : 'Added', formatDate(i.createdAt)].join(' · ');
  const edited = i.updatedAt ? ` · note edited${i.updatedBy ? ` by ${i.updatedBy}` : ''} ${formatDate(i.updatedAt)}` : '';
  return (
    <li className="space-y-1.5 py-3 first:pt-1" data-testid="case-item" data-kind={i.kind}>
      {i.entity && (
        <p className="flex flex-wrap items-center gap-x-2 text-sm">
          <EntityLink schema={snap} entity={i.entity} />
          <span className="text-xs text-muted-foreground">
            {snap.types.find(t => t.id === i.entity?.typeId)?.label} · <span className="font-mono">{i.entity.code}</span>
            {i.mergedFrom && ' · merged since it was added'}
          </span>
        </p>
      )}
      {i.document && (
        <p className="flex flex-wrap items-center gap-x-2 text-sm">
          {i.document.sensitivity === 'confidential' && (
            <Lock className="size-3.5 text-muted-foreground" aria-label="Confidential" role="img" />
          )}
          <Link to="/library/$id" params={{ id: i.document.id }} className="font-medium break-all hover:underline">
            <DataText>{i.document.title ?? i.document.filename}</DataText>
          </Link>
          <span className="text-xs text-muted-foreground">
            {DETECTED_KIND_LABELS[i.document.kind]} · {PIPELINE_STATUS_LABELS[i.document.status]}
          </span>
        </p>
      )}
      {editing ? (
        <form onSubmit={save} className="space-y-2">
          <label htmlFor={noteId} className="sr-only">
            {i.kind === 'note' ? 'Note' : `Note on ${name}`}
          </label>
          <textarea
            id={noteId}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            dir="auto"
            className={textarea}
            aria-invalid={tooLong || undefined}
            // biome-ignore lint/a11y/noAutofocus: the person just asked to edit this note
            autoFocus
          />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={tooLong || edit.isPending || (i.kind === 'note' && !draft.trim())}>
              Save note
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditing(false);
                setDraft(i.note ?? '');
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        i.note && (
          <DataBlock className={cn('whitespace-pre-wrap text-sm', i.kind !== 'note' && 'text-muted-foreground')}>{i.note}</DataBlock>
        )
      )}
      <p className="flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
        <span>
          <DataText>{by}</DataText>
          <DataText>{edited}</DataText>
        </span>
        {canWrite && !editing && (
          <>
            <button
              type="button"
              className="underline underline-offset-2 hover:text-foreground"
              onClick={() => {
                setDraft(i.note ?? '');
                setEditing(true);
              }}
            >
              {i.note ? 'Edit note' : 'Add a note'}
              <span className="sr-only"> {i.kind === 'note' ? '' : `on ${name}`}</span>
            </button>
            <button type="button" className="underline underline-offset-2 hover:text-foreground" onClick={drop}>
              Remove<span className="sr-only"> {name}</span>
            </button>
          </>
        )}
      </p>
      {error && (
        <p role="alert" className="text-xs text-danger-foreground">
          {error}
        </p>
      )}
    </li>
  );
}
